import { Link, useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AgencyContactDialog } from "@/components/agency/AgencyContactDialog";
import { AgencySummaryCard } from "@/components/agency/AgencySummaryCard";
import { ScorecardGroupDialog } from "@/components/agency/ScorecardGroupDialog";
import { useAppearance } from "@/components/layout/AppearanceProvider";
import { EmployerContactDialog } from "@/components/policy/EmployerContactDialog";
import { EndorsementsDialog } from "@/components/policy/EndorsementsDialog";
import { LocationsClassCodesDialog } from "@/components/policy/LocationsClassCodesDialog";
import { PolicySummaryCard } from "@/components/policy/PolicySummaryCard";
import { RelatedPoliciesDialog } from "@/components/policy/RelatedPoliciesDialog";
import { ServiceTeamDialog } from "@/components/policy/ServiceTeamDialog";
import { ErrorState } from "@/components/shared/error-state";
import { FavoriteToggle } from "@/components/shared/favorite-toggle";
import { type BreadcrumbTrailItem, PageBreadcrumbs } from "@/components/shared/page-breadcrumbs";
import { PanelState } from "@/components/shared/panel-state";
import { Button } from "@/components/ui/button";
import { SectionBar } from "@/components/ui/section-bar";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";
import { TabBar } from "@/components/ui/tab-bar";
import {
  usePolicy,
  usePolicyAgency,
  usePolicyClaimCounts,
  usePolicyLossRatio,
  usePolicyPeriods,
  usePolicyStates,
} from "@/hooks/use-policy";
import { useSyncUserDisplay } from "@/hooks/use-user-display";
import { lossRatioColor } from "@/lib/appearance";
import { parseFrom } from "@/lib/breadcrumb-from";
import { formatCurrency, formatDate, formatShortDate } from "@/lib/format";
import { policyStatusKey } from "@/lib/status-colors";
import { cn } from "@/lib/utils";
import { policyDashboardSearchSchema } from "@/schemas/search";

function Field({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="min-w-0">
      <div className="text-muted-foreground text-[11px] font-medium">
        {hint ? (
          <span
            title={hint}
            className="cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-2"
          >
            {label}
          </span>
        ) : (
          label
        )}
      </div>
      <div className="mt-0.5 break-words font-medium text-sm">{value ?? "—"}</div>
    </div>
  );
}

function SubNavButton({
  to,
  params,
  children,
}: {
  to: React.ComponentProps<typeof Link>["to"];
  params?: React.ComponentProps<typeof Link>["params"];
  children: React.ReactNode;
}) {
  return (
    <Button variant="outline" size="sm" className="h-7 rounded-md px-2.5 text-xs" asChild>
      <Link to={to} params={params}>
        {children}
      </Link>
    </Button>
  );
}

function LossRatioBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-md border bg-card">
      <div className="border-b bg-muted/40 px-3 py-1.5 text-foreground/80 text-xs">{title}</div>
      <div className="flex flex-col items-center px-3 py-3 text-center">{children}</div>
    </div>
  );
}

function LossRatioValue({ earned, ratio }: { earned: number; ratio: number }) {
  const { appearance } = useAppearance();
  // No earned premium = no defined ratio. Render an em-dash whether the
  // incurred side is zero ("no activity") or positive ("losses but no
  // premium") — either way the percentage is mathematically undefined.
  if (earned === 0) {
    return (
      <div
        className="font-bold text-2xl text-muted-foreground"
        title="No earned premium — loss ratio not meaningful"
      >
        —
      </div>
    );
  }
  // Real ratio against real earned premium — color via the user-tunable
  // band scheme so the value reads green / amber / red at a glance. 0%
  // lands in the low band (clean policy = green).
  return (
    <div
      className="font-bold text-2xl"
      style={{ color: lossRatioColor(ratio, appearance.lossRatio) }}
    >
      {ratio}%
    </div>
  );
}

type PolicyInfoTab = "Policyholder" | "Basics" | "Financials" | "Deductible" | "State Details";
const POLICY_INFO_TABS: PolicyInfoTab[] = [
  "Basics",
  "Policyholder",
  "State Details",
  "Financials",
  "Deductible",
];

export function PolicyDashboardPage() {
  const { policyNumber } = useParams({ strict: false }) as { policyNumber: string };
  // Re-parse with the same Zod schema the route uses so a malformed `?term=`
  // can't blow up to the error boundary.
  const search = policyDashboardSearchSchema.safeParse(useSearch({ strict: false }));
  const termFromSearch = search.success ? search.data.term : undefined;
  const fromHint = search.success ? parseFrom(search.data.from) : null;
  const [selectedTerm, setSelectedTerm] = useState<string | undefined>(termFromSearch);
  const [selectedStateCode, setSelectedStateCode] = useState<string | undefined>(undefined);
  const [scorecardOpen, setScorecardOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [serviceTeamOpen, setServiceTeamOpen] = useState(false);
  const [employerContactOpen, setEmployerContactOpen] = useState(false);
  const [endorsementsOpen, setEndorsementsOpen] = useState(false);
  const [relatedOpen, setRelatedOpen] = useState(false);
  const [locationsAllOpen, setLocationsAllOpen] = useState(false);
  const [locationsStateOpen, setLocationsStateOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<PolicyInfoTab>("Basics");

  // Router can reuse the route component instance across policyNumber changes,
  // so reset per-policy state whenever the user navigates to a different policy.
  // Tab/term/state are reset so the new policy resolves its own defaults; dialog
  // open flags are reset so a Service Team or Endorsements dialog left open on
  // the previous policy doesn't reappear on the new one with stale data.
  useEffect(() => {
    if (policyNumber) {
      setActiveTab("Basics");
      // Honor `?term=` deep links across policy navigations: drop the previous
      // policy's selectedTerm but pick up the new policy's URL term if present.
      setSelectedTerm(termFromSearch);
      setSelectedStateCode(undefined);
      setScorecardOpen(false);
      setContactOpen(false);
      setServiceTeamOpen(false);
      setEmployerContactOpen(false);
      setEndorsementsOpen(false);
      setRelatedOpen(false);
      setLocationsAllOpen(false);
      setLocationsStateOpen(false);
    }
  }, [policyNumber, termFromSearch]);

  const { data: policy, isLoading, isError, refetch } = usePolicy(policyNumber, selectedTerm);
  const periodsQuery = usePolicyPeriods(policyNumber);
  const agencyQuery = usePolicyAgency(policyNumber);
  const periods = periodsQuery.data;
  const agencyData = agencyQuery.data;

  // Only sync once the policy actually loads — visiting a bogus number
  // shouldn't pollute UserDisplay with a non-existent policy.
  useSyncUserDisplay({
    policyNumber: policy ? policyNumber : null,
    policyholderName: policy?.insuredName ?? null,
  });
  // policy.effectiveDate is string | null from the API schema; the hooks
  // take string | undefined, so coerce null → undefined here.
  const termForScopedQueries = selectedTerm ?? policy?.effectiveDate ?? undefined;
  const lossRatioQuery = usePolicyLossRatio(policyNumber, termForScopedQueries);
  const lossRatio = lossRatioQuery.data;
  const statesQuery = usePolicyStates(policyNumber, termForScopedQueries);
  const states = statesQuery.data;
  const claimCounts = usePolicyClaimCounts(policyNumber).data;

  // When states load (or term changes), default the selected state to the first one
  // if the current selection isn't in the new list.
  useEffect(() => {
    if (!states || states.length === 0) {
      if (selectedStateCode !== undefined) setSelectedStateCode(undefined);
      return;
    }
    if (!selectedStateCode || !states.some((s) => s.stateCode === selectedStateCode)) {
      setSelectedStateCode(states[0]?.stateCode);
    }
  }, [states, selectedStateCode]);

  const selectedState = states?.find((s) => s.stateCode === selectedStateCode) ?? null;

  // The Deductible tab only appears when the active term has at least one
  // deductible field. If the user has it selected and switches to a term
  // without coverage, bounce back to Basics so the panel area doesn't go
  // blank.
  const hasDeductible = Boolean(
    policy?.deductibleTypeLabel || policy?.deductibleLimit || policy?.deductibleAggregate,
  );
  // Derive synchronously so the new render uses the bounced tab the moment
  // fresh policy data lands. The useEffect below keeps `activeTab` state in
  // lockstep so subsequent term switches behave correctly, but rendering
  // off `effectiveTab` avoids the one-frame flash where the Deductible tab
  // is still highlighted and an empty panel renders before the effect runs.
  const effectiveTab: PolicyInfoTab =
    activeTab === "Deductible" && policy && !hasDeductible ? "Basics" : activeTab;
  useEffect(() => {
    if (activeTab === "Deductible" && policy && !hasDeductible) {
      setActiveTab("Basics");
    }
  }, [activeTab, policy, hasDeductible]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-5 w-48" />
        <div className="grid gap-4 lg:grid-cols-12">
          <div className="space-y-3 rounded-md border bg-card p-4 lg:col-span-5">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-3/4" />
            <div className="grid grid-cols-2 gap-3 pt-2">
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
            </div>
          </div>
          <div className="space-y-3 rounded-md border bg-card p-4 lg:col-span-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </div>
          <div className="space-y-3 rounded-md border bg-card p-4 lg:col-span-4">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return <ErrorState message="Failed to load policy." onRetry={() => refetch()} />;
  }

  if (!policy) {
    return (
      <div className="space-y-3">
        <PageBreadcrumbs current="Policy Not Found" />
        <div className="py-12 text-center">
          <h2 className="font-semibold text-xl">Policy Not Found</h2>
          <p className="mt-2 text-muted-foreground">No policy found with number {policyNumber}.</p>
          <Button variant="outline" className="mt-4" asChild>
            <Link to="/home">Back to Search</Link>
          </Button>
        </div>
      </div>
    );
  }

  const agent = agencyData?.agent;
  const agency = agencyData?.agency;
  // Dates wire-serialize to ISO strings via Response.json. The legacy /api/
  // endpoint returns either an ISO string or null; we already short-circuit
  // above when policy is missing, so effectiveDate may still be null on the
  // policy row itself if the legacy query has no value.
  const activeTermEffective = policy.effectiveDate ?? "";
  const currentPeriod = periods?.find((p) => p.effectiveDate === activeTermEffective);

  const policyPeriodLabel = `${formatDate(policy.effectiveDate)} – ${formatDate(policy.expirationDate)}`;

  return (
    <div className="space-y-3">
      <PageBreadcrumbs
        trail={
          fromHint?.kind === "agency"
            ? ([
                {
                  key: "agency",
                  link: (
                    <Link to="/agency/$agentNumber" params={{ agentNumber: fromHint.agentNumber }}>
                      Agency {fromHint.agentNumber}
                    </Link>
                  ),
                },
                {
                  key: "policies",
                  link: (
                    <Link to="/policy/list" search={{ agencyCode: fromHint.agentNumber }}>
                      Policies
                    </Link>
                  ),
                },
              ] satisfies BreadcrumbTrailItem[])
            : undefined
        }
        current={`Policy ${policyNumber}`}
      />

      {/* Main 3-column body — each column's section bar attaches directly to
          its body card (no gap between header and content). Columns fade /
          slide in with a small per-column delay so the page composes itself
          on load instead of popping all at once. */}
      <div className="grid gap-4 md:grid-cols-12">
        {/* ─── COL 1: Policyholder + Agency ─── */}
        <div
          key={`col1-${policyNumber}`}
          className="space-y-4 animate-in fade-in slide-in-from-bottom-4 [animation-duration:800ms] [animation-fill-mode:backwards] md:col-span-3"
        >
          {/* Policyholder */}
          <div>
            <SectionBar
              title={`Policy No. ${policyNumber}`}
              copyValue={policyNumber}
              copyLabel={`policy ${policyNumber}`}
              rightSlot={
                <FavoriteToggle kind="policy" number={policyNumber} name={policy.insuredName} />
              }
            />
            <div className="rounded-b-md border border-t-0 bg-card p-4">
              <PolicySummaryCard
                policyNumber={policyNumber}
                insuredName={policy.insuredName}
                insuredAddress={claimCounts?.insuredAddress ?? null}
                inceptionDate={policy.inceptionDate}
                expirationDate={policy.expirationDate}
                claimCounts={claimCounts}
                actions={
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 rounded-md px-2.5 text-xs"
                      onClick={() => setServiceTeamOpen(true)}
                    >
                      Service Team
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 rounded-md px-2.5 text-xs"
                      onClick={() =>
                        toast.info(
                          "Claim Service Summary opens in an external application (coming soon)",
                        )
                      }
                    >
                      Claim Service Summary
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 rounded-md px-2.5 text-xs"
                      onClick={() => setEmployerContactOpen(true)}
                    >
                      Employer Contact
                    </Button>
                  </>
                }
              />
            </div>
          </div>

          {/* Agency */}
          <SectionBar
            title={agent ? `Agency No. ${agent.agentNumber}` : "Agency"}
            linkTo={agent ? "/agency/$agentNumber" : undefined}
            linkParams={agent ? { agentNumber: agent.agentNumber } : undefined}
            copyValue={agent?.agentNumber}
            copyLabel={agent ? `agency ${agent.agentNumber}` : undefined}
            rightSlot={
              agent ? (
                <FavoriteToggle
                  kind="agency"
                  number={agent.agentNumber}
                  name={agency?.agencyName ?? null}
                />
              ) : undefined
            }
          />
          <div className="-mt-4 rounded-md rounded-t-none border border-t-0 bg-card p-4">
            {agencyQuery.isLoading ? (
              <PanelState loading loadingText="Loading agency…" />
            ) : agencyQuery.isError ? (
              <PanelState
                error={agencyQuery.error}
                errorMessage="Failed to load agency."
                onRetry={() => agencyQuery.refetch()}
              />
            ) : agent ? (
              <AgencySummaryCard
                agent={agent}
                agency={agency}
                actions={
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 rounded-md px-2.5 text-xs"
                      onClick={() => setScorecardOpen(true)}
                    >
                      Scorecard Group
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 rounded-md px-2.5 text-xs"
                      onClick={() => setContactOpen(true)}
                    >
                      Agency Contact
                    </Button>
                  </>
                }
              />
            ) : (
              <PanelState empty emptyMessage="No agency on file for this policy." />
            )}
          </div>
        </div>

        {/* ─── COL 2: Term list + Loss Ratio ─── */}
        <div
          key={`col2-${policyNumber}`}
          className="space-y-4 animate-in fade-in slide-in-from-bottom-4 [animation-duration:800ms] [animation-fill-mode:backwards] [animation-delay:200ms] md:col-span-3"
        >
          {/* Term selector */}
          <div>
            <SectionBar
              title="Policy Period"
              subtitle={periods ? `${periods.length} rows` : undefined}
            />
            <div className="overflow-hidden rounded-b-md border border-t-0 bg-card">
              {periodsQuery.isLoading ? (
                <PanelState loading loadingText="Loading periods…" className="px-4" />
              ) : periodsQuery.isError ? (
                <PanelState
                  error={periodsQuery.error}
                  errorMessage="Failed to load periods."
                  onRetry={() => periodsQuery.refetch()}
                  className="px-4"
                />
              ) : periods && periods.length > 0 ? (
                <ul className="max-h-[420px] divide-y overflow-y-auto">
                  {periods.map((p) => {
                    const eff = p.effectiveDate;
                    const isSelected = eff === activeTermEffective;
                    return (
                      <li key={eff}>
                        <button
                          type="button"
                          onClick={() => setSelectedTerm(eff)}
                          className={cn(
                            "w-full px-3 py-2 text-left text-sm transition-colors hover:bg-accent",
                            isSelected && "bg-primary/10 font-medium text-primary",
                          )}
                        >
                          {formatDate(p.effectiveDate)} – {formatDate(p.expirationDate)}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <PanelState empty emptyMessage="No periods on file." className="px-4" />
              )}
            </div>
          </div>

          {/* Loss Ratio — three framed cards: term EP, current-year CY,
              current-year AY. The header arrow + the "Loss Ratio Detail"
              button both link to the policy-wide detail page. */}
          <div>
            <SectionBar
              title="Loss Ratio"
              linkTo="/policy/$policyNumber/loss-ratio"
              linkParams={{ policyNumber }}
            />
            <div className="rounded-b-md border border-t-0 bg-card p-4">
              {lossRatioQuery.isLoading ? (
                <PanelState loading loadingText="Loading loss ratio…" />
              ) : lossRatioQuery.isError ? (
                <PanelState
                  error={lossRatioQuery.error}
                  errorMessage="Failed to load loss ratio."
                  onRetry={() => lossRatioQuery.refetch()}
                />
              ) : lossRatio ? (
                <div className="space-y-3">
                  <div>
                    <SubNavButton to="/policy/$policyNumber/loss-ratio" params={{ policyNumber }}>
                      Loss Ratio Detail
                    </SubNavButton>
                  </div>
                  <LossRatioBox
                    title={`${formatShortDate(policy.effectiveDate)} - ${formatShortDate(policy.expirationDate)} Loss Ratio`}
                  >
                    <span className="text-muted-foreground text-xs">EP Loss Ratio</span>
                    <LossRatioValue earned={lossRatio.totalEarned} ratio={lossRatio.lossRatio} />
                  </LossRatioBox>
                  <LossRatioBox title={`${lossRatio.anchorYear} Calendar Year Loss Ratio`}>
                    <LossRatioValue
                      earned={lossRatio.currentYear.earnedPremium}
                      ratio={lossRatio.currentYear.calendar.lossRatio}
                    />
                  </LossRatioBox>
                  <LossRatioBox title={`${lossRatio.anchorYear} Accident Year Loss Ratio`}>
                    <LossRatioValue
                      earned={lossRatio.currentYear.earnedPremium}
                      ratio={lossRatio.currentYear.accident.lossRatio}
                    />
                  </LossRatioBox>
                </div>
              ) : (
                <PanelState empty emptyMessage="No loss ratio data on file." />
              )}
            </div>
          </div>
        </div>

        {/* ─── COL 3: States + Policy Information ─── */}
        <div
          key={`col3-${policyNumber}`}
          className="space-y-4 animate-in fade-in slide-in-from-bottom-4 [animation-duration:800ms] [animation-fill-mode:backwards] [animation-delay:400ms] md:col-span-6"
        >
          {/* States — click a state to filter the Policy Information panel below. */}
          <div>
            <SectionBar title={`${policyPeriodLabel} States`} />
            <div className="rounded-b-md border border-t-0 bg-card p-4">
              {statesQuery.isError ? (
                <PanelState
                  error={true}
                  errorMessage="Failed to load covered states."
                  onRetry={() => statesQuery.refetch()}
                />
              ) : states && states.length > 0 ? (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                  {states.map((s) => {
                    const isSelected = s.stateCode === selectedStateCode;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setSelectedStateCode(s.stateCode)}
                        className={cn(
                          "overflow-hidden rounded-md border text-left transition-colors",
                          isSelected
                            ? "border-primary ring-1 ring-primary"
                            : "border-border hover:border-primary/50",
                        )}
                      >
                        <div
                          className={cn(
                            "px-2.5 py-1 font-medium text-xs",
                            isSelected
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-foreground",
                          )}
                        >
                          {s.stateName ?? s.stateCode}
                        </div>
                        <div className="space-y-1 bg-card px-2.5 py-2">
                          <div>
                            <div className="text-[10px] text-muted-foreground font-medium">
                              Schedule Rating
                            </div>
                            <div className="font-medium text-sm">{s.scheduleRating ?? "—"}</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-muted-foreground font-medium">
                              Written Premium
                            </div>
                            <div className="font-medium text-sm">
                              {formatCurrency(s.writtenPremium)}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <PanelState empty emptyMessage="No covered states on file." />
              )}
            </div>
          </div>

          {/* Policy Information */}
          <div>
            <SectionBar
              title={`${policyPeriodLabel}${selectedState ? ` — ${selectedState.stateName ?? selectedState.stateCode}` : ""} Policy Information`}
              subtitle={currentPeriod?.policyStatus ?? policy.policyStatus}
            />
            <div className="space-y-5 rounded-b-md border border-t-0 bg-card p-4">
              <div className="flex flex-wrap gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 rounded-md px-2.5 text-xs"
                  onClick={() => setEndorsementsOpen(true)}
                >
                  Endorsements
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 rounded-md px-2.5 text-xs"
                  onClick={() => setRelatedOpen(true)}
                >
                  Related Policies
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 rounded-md px-2.5 text-xs"
                  onClick={() => setLocationsAllOpen(true)}
                >
                  All Locations / Class Codes
                </Button>
              </div>

              {/* Tab navigation. Hide Deductible when the policy has no
                  deductible coverage (all three deductible fields are null). */}
              <TabBar
                ariaLabel="Policy information"
                activeKey={effectiveTab}
                onChange={setActiveTab}
                tabs={POLICY_INFO_TABS.filter((tab) => tab !== "Deductible" || hasDeductible).map(
                  (tab) => ({ key: tab, label: tab }),
                )}
              />

              {effectiveTab === "Policyholder" && (
                <section className="pt-4">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <Field label="Number of Employees" value={policy.noEmployees} />
                    <Field label="Governing Class Code" value={policy.governingClassCode} />
                    <Field
                      label="Class Code Description"
                      value={policy.governingClassDescription}
                    />
                    <Field label="Entity Type" value={policy.insuredType} />
                    <Field
                      label="FEIN"
                      value={policy.feinTaxId}
                      hint="Federal Employer Identification Number — the IRS tax ID for this employer."
                    />
                    <Field label="Description of Operation" value={policy.businessDescription} />
                    <Field
                      label="NAICS"
                      value={policy.naicsCode}
                      hint="North American Industry Classification System code — describes the employer's industry."
                    />
                    <Field
                      label="EPLI Coverage"
                      value={policy.epliCoverage}
                      hint="Employment Practices Liability Insurance — coverage for wrongful-termination, harassment, and discrimination claims."
                    />
                  </div>
                </section>
              )}

              {effectiveTab === "Basics" && (
                <section className="pt-4">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <Field label="Policy No." value={policy.policyNumber} />
                    <Field
                      label="Policy Status"
                      value={
                        policy.policyStatus ? (
                          <StatusBadge
                            statusKey={policyStatusKey(policy.policyStatus)}
                            label={policy.policyStatus}
                          />
                        ) : null
                      }
                    />
                    <Field label="Cancellation Date" value={formatDate(policy.cancellationDate)} />
                    <Field label="Cancellation Reason" value={policy.cancelDescription} />
                    <Field label="Rating Tier" value={policy.ratingTier} />
                    <Field label="Policy Period" value={policyPeriodLabel} />
                    <Field label="Liability Limits" value={policy.liabilityLimits} />
                    <Field label="Pay Plan" value={policy.paymentPlan} />
                    <Field label="Subrogation Waiver" value={policy.subrogationWaiver} />
                    <Field label="Exclusion Waiver" value={policy.exclusionWaiver} />
                    <Field
                      label="NCCI State Number"
                      value={policy.ncciStateNumber}
                      hint="National Council on Compensation Insurance — the state-issued ID number used in workers' comp regulatory filings."
                    />
                  </div>
                </section>
              )}

              {effectiveTab === "Financials" && (
                <section className="pt-4">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <Field
                      label="EMOD"
                      value={policy.emod}
                      hint="Experience Modification Factor — a multiplier applied to the policy premium based on the employer's claims history. 1.0 is average; above 1.0 raises premium, below lowers it."
                    />
                    <Field
                      label="Term Written Premium"
                      value={formatCurrency(lossRatio?.totalWritten)}
                    />
                    <Field
                      label="Policy Dividend Paid"
                      value={formatCurrency(policy.policyDividendPaid)}
                    />
                  </div>
                </section>
              )}

              {effectiveTab === "Deductible" && (
                <section className="pt-4">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <Field label="Deductible Type" value={policy.deductibleTypeLabel} />
                    <Field label="Deductible Limit" value={policy.deductibleLimit} />
                    <Field label="Aggregate Limit" value={policy.deductibleAggregate} />
                  </div>
                </section>
              )}

              {effectiveTab === "State Details" &&
                (selectedState ? (
                  <>
                    <div className="flex flex-wrap gap-1.5 pt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 rounded-md px-2.5 text-xs"
                        onClick={() => setLocationsStateOpen(true)}
                      >
                        Locations / Class Codes —{" "}
                        {selectedState.stateName ?? selectedState.stateCode}
                      </Button>
                    </div>
                    <section>
                      <h3 className="mb-3 font-medium text-primary text-sm">State Details</h3>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field
                          label="State"
                          value={`${selectedState.stateCode}${selectedState.stateName ? ` — ${selectedState.stateName}` : ""}`}
                        />
                        <Field
                          label="State Written Premium"
                          value={formatCurrency(selectedState.writtenPremium)}
                        />
                      </div>
                    </section>
                  </>
                ) : (
                  <div className="py-8 text-center text-muted-foreground text-sm">
                    Select a state from the tiles above to see state-specific details.
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      {agent?.agentNumber && (
        <>
          <ScorecardGroupDialog
            agentNumber={agent.agentNumber}
            open={scorecardOpen}
            onOpenChange={setScorecardOpen}
          />
          <AgencyContactDialog
            agentNumber={agent.agentNumber}
            open={contactOpen}
            onOpenChange={setContactOpen}
          />
        </>
      )}
      <ServiceTeamDialog
        policyNumber={policyNumber}
        open={serviceTeamOpen}
        onOpenChange={setServiceTeamOpen}
      />
      <EmployerContactDialog
        policyNumber={policyNumber}
        open={employerContactOpen}
        onOpenChange={setEmployerContactOpen}
      />
      <EndorsementsDialog
        policyNumber={policyNumber}
        open={endorsementsOpen}
        onOpenChange={setEndorsementsOpen}
      />
      <RelatedPoliciesDialog
        policyNumber={policyNumber}
        open={relatedOpen}
        onOpenChange={setRelatedOpen}
      />
      <LocationsClassCodesDialog
        policyNumber={policyNumber}
        open={locationsAllOpen}
        onOpenChange={setLocationsAllOpen}
      />
      {selectedState && (
        <LocationsClassCodesDialog
          policyNumber={policyNumber}
          open={locationsStateOpen}
          onOpenChange={setLocationsStateOpen}
          stateCode={selectedState.stateCode}
          stateName={selectedState.stateName ?? undefined}
        />
      )}
    </div>
  );
}
